import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInDays } from 'date-fns';

import { createTemplate } from 'actions/templates';
import { fetchResourceChildrenForTemplate } from 'api/templates-api';

import { trackEvent } from 'services/events-tracker-service';
import { handleServerError } from 'shared-services/error-service';
import { TEMPLATE_FIELDS_MAP } from 'constants/template';

import FieldDefault from 'components/field-default';
import FieldTextarea from 'components/field-textarea';
import ModalHeader from 'components/ModalHeader';
import FieldLabel from 'components/FieldLabel';
import InfoBox from 'components/info-box';

import TemplateOptions from './TemplateOptions';
import ResourceFields from './ResourceFields';
import ResourceChildren from './ResourceChildren';

import { Template, TemplateField, TemplateChildResource } from 'types';

import './CreateTemplate.styles.css';

const RESOURCE_CHILDREN_ORDER = ['milestone', 'task', 'subtask'];

interface CreateTemplateProps {
    resourceId: Template['resource_id'],
    type: Template['type'],
    name: Template['name'],
    disabledOptions: Template['options'],
    fields: {
        type: TemplateField['type'],
        value: string
    }[],
    close: () => void
}

const CreateTemplate = ({ resourceId, type, name, disabledOptions, fields, close }: CreateTemplateProps) => {
    const { t } = useTranslation(['template', 'common', 'date']);
    const [nameState, setNameState] = useState(name || '');
    const [descriptionState, setDescriptionState] = useState('');
    const [optionsState, setOptionsState] = useState<Template['options']>([]);
    const [loading, setLoading] = useState(false);
    const [childResources, setChildResources] = useState<TemplateChildResource[]>([]);
    const [currentResourceFields, setCurrentResourceFields] = useState<TemplateField[]>(fields ? fields.map(field => ({
        type: field.type,
        offset: field.value ? differenceInDays(new Date(field.value), new Date()) : null
    })) : []);

    const populated = useMemo(() => {
        return name !== '';
    }, [name]);

    const computedCurrentResourceFields = useMemo(() => {
        return currentResourceFields.filter(({ type }) => optionsState.includes(type))
    }, [currentResourceFields, optionsState]);

    const hasFields = useMemo(() => {
        return computedCurrentResourceFields.length || childResources.length;
    }, [computedCurrentResourceFields.length, childResources.length]);

    const fetchChildResources = useCallback(async () => {
        const children = await fetchResourceChildrenForTemplate(type, resourceId);
        setChildResources(
            children
                .map((resource: any) => ({
                    ...resource,
                    fields: resource.fields.map((field: any) => ({
                        type: field.type,
                        offset: field.value ? differenceInDays(new Date(field.value), new Date()) : null
                    }))
                }))
                .sort((a: TemplateChildResource, b: TemplateChildResource) => {
                    let aIndex = RESOURCE_CHILDREN_ORDER.indexOf(a.type);
                    let bIndex = RESOURCE_CHILDREN_ORDER.indexOf(b.type);

                    if (aIndex === bIndex) {
                        if (a.type === 'milestone') {
                            return (a.fields.find(f => f.type === 'start')?.offset || 0) - (b.fields.find(f => f.type === 'start')?.offset || 0);
                        }

                        if (a.name.toLowerCase() < b.name.toLowerCase()) {
                            return -1;
                        }

                        if (a.name.toLowerCase() > b.name.toLowerCase()) {
                            return 1;
                        }

                        return 0;
                    }

                    return aIndex - bIndex;
                })
        );
    }, [resourceId, type]);

    const handleSuccessClick = useCallback(async () => {
        setLoading(true);
        try {
            await createTemplate({
                type,
                resource_id: resourceId,
                name: nameState,
                description: descriptionState,
                options: optionsState,
                fields: currentResourceFields,
                children: childResources
            });

            close();
        } catch (e) {
            handleServerError(e, {}, false);
        }

        setLoading(false);
    }, [resourceId, type, nameState, descriptionState, optionsState, currentResourceFields, childResources]);

    const handleChildChange = useCallback((offset: TemplateField['offset'], type: TemplateField['type'], id: string) => {
        setChildResources(prevState => [...prevState].map(item => {
            if (item.id !== id) {
                return item;
            }

            return {
                ...item,
                fields: [...item.fields].map(field => {
                    if (field.type !== type) {
                        return field;
                    }

                    return {
                        ...field,
                        offset
                    };
                })
            };
        }));
    }, []);

    const handleCurrentResourceOffsetChange = useCallback((offset: TemplateField['offset'], type: TemplateField['type'], id: string) => {
        setCurrentResourceFields(prevState => [...prevState].map(field => {
            if (field.type !== type) {
                return field;
            }

            return {
                ...field,
                offset
            };
        }))
    }, []);

    useEffect(() => {
        fetchChildResources();
    }, [fetchChildResources]);

    return (
        <div className={'modal modal-default' + (hasFields ? ' modal-xxlarge' : '')}>
            <div className="modal-main">
                <div className="modal-inner">
                    <ModalHeader
                        title={ t('create.title') }
                        fluid={true}
                        onClose={close}
                    />

                    <div className="modal-body fixed-height">
                        <div className="create-template">
                            <div className="create-template-side">
                                <div className="form">
                                    <div className="form-row">
                                        <label className="form-label-alt">{ t('create.name.label') }</label>

                                        <FieldDefault
                                            value={nameState}
                                            placeholder={ t('create.name.placeholder') }
                                            onChange={setNameState}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <label className="form-label-alt">{ t('create.description.label') }</label>

                                        <FieldTextarea
                                            value={descriptionState}
                                            max={200}
                                            onChange={setDescriptionState}
                                            placeholder={ t('create.description.label') }
                                        />
                                    </div>

                                    <div className="form-row">
                                        <label className="form-label-alt">{ t('create.options.label') }</label>

                                        <TemplateOptions type={type} disabledOptions={disabledOptions} onChange={setOptionsState} />
                                    </div>
                                </div>
                            </div>

                            {
                                hasFields ? (
                                    <div className="create-template-side">
                                        <InfoBox
                                            id="createTemplateChildrenHelp"
                                            saveState={true}
                                            content={[
                                                {
                                                    body: type === 'task' ? t('create.childrenHelp.tasks') : t('create.childrenHelp.milestonesAndTasks')
                                                }
                                            ]}
                                        />

                                        <div className="template-resource-fields">
                                            <div className="fields-head">
                                                <div className="fields-row">
                                                    <div className="fields-cell cell-xxs" />

                                                    <div className="fields-cell cell-xs" />

                                                    <div className="fields-cell align-left">
                                                        { t('common:Name') }
                                                    </div>

                                                    <div className="fields-cell cell-s">
                                                        { t('offset.start') }

                                                        <span className="tag-pill tag-pill-alt">{ t('date:days') }</span>
                                                    </div>

                                                    <div className="fields-cell cell-s">
                                                        { t('offset.end') }

                                                        <span className="tag-pill tag-pill-alt">{ t('date:days') }</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="fields-body">
                                                {
                                                    type !== 'document' && type !== 'project' && computedCurrentResourceFields.length ? (
                                                        <ResourceFields
                                                            id={resourceId}
                                                            name={name}
                                                            type={type}
                                                            fields={computedCurrentResourceFields}
                                                            onChange={handleCurrentResourceOffsetChange}
                                                        />
                                                    ) : null
                                                }

                                                <ResourceChildren resourceId={resourceId} resources={childResources} onChange={handleChildChange} />
                                            </div>
                                        </div>
                                    </div>
                                ) : null
                            }
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={close}>{ t('common:Cancel') }</button>

                        <button className={'btn btn-success btn-has-loading' + (loading ? ' loading' : '')} disabled={!populated} onClick={handleSuccessClick}>{ t('common:Create') }</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateTemplate;
